import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { authService, AuthUser, AuthSession } from '@/lib/authService';
import { api } from '@/lib/apiClient';
import logger from '@/lib/logger';

interface AuthContextType {
  user: AuthUser | null;
  userId: string | null;
  session: AuthSession | null;
  profile: any | null;
  userRole: string | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, tag?: string, employeeId?: string, operatorArea?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchProfile = async (userId: string) => {
    try {
      const { data: profileData, error: profileError } = await api
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      const { data: rolesData, error: rolesError } = await api
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (rolesError) throw rolesError;

      setProfile(profileData);
      setUserRole(rolesData && rolesData.length > 0 ? rolesData[0].role : 'operator');
    } catch (error: any) {
      logger.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      const currentSession = authService.getSession();
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        await fetchProfile(currentSession.user.id);
      }

      setLoading(false);
    };

    checkSession();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, tag?: string, employeeId?: string, operatorArea?: string) => {
    try {
      // Crear usuario usando RPC para hashear la contraseña
      const { data, error } = await api.rpc('create_user', {
        p_email: email,
        p_password: password,
        p_full_name: fullName,
        p_tag: tag || null,
        p_employee_id: employeeId || null,
        p_operator_area: operatorArea || null,
      });

      if (error) {
        toast({
          title: "Error al registrarse",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      toast({
        title: "¡Registro exitoso!",
        description: "Tu cuenta ha sido creada correctamente.",
      });

      return { error: null };
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Ocurrió un error al crear la cuenta.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { session: newSession, error } = await authService.signIn(email, password);

      if (error || !newSession) {
        toast({
          title: "Error al iniciar sesión",
          description: error?.message || "Usuario o contraseña incorrectos",
          variant: "destructive",
        });
        return { error };
      }

      // Update state
      setSession(newSession);
      setUser(newSession.user);

      // Fetch profile and role
      await fetchProfile(newSession.user.id);

      toast({
        title: "¡Bienvenido!",
        description: "Has iniciado sesión correctamente.",
      });

      navigate('/');
      return { error: null };
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Ocurrió un error al iniciar sesión.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setSession(null);
      setProfile(null);
      setUserRole(null);

      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión correctamente.",
      });

      navigate('/auth');
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Ocurrió un error al cerrar sesión.",
        variant: "destructive",
      });
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userId: user?.id ?? null,
        session,
        profile,
        userRole,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
